import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  ThumbsUp, 
  ThumbsDown, 
  Meh,
  TrendingUp,
  TrendingDown,
  Download,
  Search,
  MessageSquare,
  Users,
  Target,
  BarChart3,
  Lightbulb,
} from "lucide-react";
import { useCohortFilterStore, useOrgFilterStore } from '@/hooks/useOrgFilterStore';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

// Mock organizations
const organizations = [
  { id: "all", name: "Todas las empresas" },
  { id: "org1", name: "Tech Corp" },
  { id: "org2", name: "Innovate SA" },
  { id: "org3", name: "Digital Labs" },
];

// Mock cohorts
const cohortsByOrg: Record<string, { id: string; name: string }[]> = {
  all: [
    { id: "all", name: "Todas las cohortes" },
    { id: "cohort-1", name: "Cohorte Enero 2025" },
    { id: "cohort-2", name: "Cohorte Marzo 2025" },
    { id: "cohort-3", name: "Cohorte Junio 2025" },
  ],
  org1: [
    { id: "all", name: "Todas las cohortes" },
    { id: "cohort-1", name: "Cohorte Enero 2025" },
    { id: "cohort-2", name: "Cohorte Marzo 2025" },
  ],
  org2: [
    { id: "all", name: "Todas las cohortes" },
    { id: "cohort-1", name: "Cohorte Enero 2025" },
  ],
  org3: [
    { id: "all", name: "Todas las cohortes" },
    { id: "cohort-3", name: "Cohorte Junio 2025" },
  ],
};

// NPS Survey responses interface
interface NPSResponse {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  company: string;
  companyId: string;
  cohort: string;
  cohortId: string;
  area: string;
  score: number; // 1-10
  reason: string; // ¿Cuál es la principal razón de tu calificación?
  improvement: string; // ¿Qué podríamos mejorar?
  type: "promoter" | "passive" | "detractor";
  submittedAt: string;
}

// Mock NPS responses with the 3 questions
const mockNPSResponses: NPSResponse[] = [
  {
    id: "nps-1",
    userId: "u1",
    userName: "María González",
    userEmail: "maria@techcorp.com",
    company: "Tech Corp",
    companyId: "org1",
    cohort: "Cohorte Enero 2025",
    cohortId: "cohort-1",
    area: "Tecnología",
    score: 10,
    reason: "El contenido es muy práctico y aplicable a mi trabajo diario. Los casos de uso son relevantes para mi industria.",
    improvement: "Me gustaría tener más contenido sobre automatización avanzada con n8n y Make.",
    type: "promoter",
    submittedAt: "2025-01-15T10:30:00Z",
  },
  {
    id: "nps-2",
    userId: "u2",
    userName: "Carlos Ruiz",
    userEmail: "carlos@innovatesa.com",
    company: "Innovate SA",
    companyId: "org2",
    cohort: "Cohorte Enero 2025",
    cohortId: "cohort-1",
    area: "Operaciones",
    score: 9,
    reason: "Los mentores tienen un conocimiento profundo y explican muy bien los conceptos técnicos.",
    improvement: "Sería útil tener sesiones de mentoría individuales más frecuentes.",
    type: "promoter",
    submittedAt: "2025-01-14T14:20:00Z",
  },
  {
    id: "nps-3",
    userId: "u3",
    userName: "Ana Martínez",
    userEmail: "ana@digitallabs.com",
    company: "Digital Labs",
    companyId: "org3",
    cohort: "Cohorte Junio 2025",
    cohortId: "cohort-3",
    area: "Marketing",
    score: 8,
    reason: "Buen ruta en general, las herramientas de IA recomendadas son muy útiles para mi trabajo.",
    improvement: "Me gustaría más ejemplos específicos de marketing digital con IA.",
    type: "passive",
    submittedAt: "2025-01-13T09:15:00Z",
  },
  {
    id: "nps-4",
    userId: "u4",
    userName: "Luis Hernández",
    userEmail: "luis@techcorp.com",
    company: "Tech Corp",
    companyId: "org1",
    cohort: "Cohorte Marzo 2025",
    cohortId: "cohort-2",
    area: "Finanzas",
    score: 5,
    reason: "El contenido es muy técnico para mi perfil, me costó seguir algunos módulos.",
    improvement: "Ofrecer tracks diferenciados por nivel de experiencia técnica.",
    type: "detractor",
    submittedAt: "2025-01-12T16:45:00Z",
  },
  {
    id: "nps-5",
    userId: "u5",
    userName: "Sofia Torres",
    userEmail: "sofia@techcorp.com",
    company: "Tech Corp",
    companyId: "org1",
    cohort: "Cohorte Enero 2025",
    cohortId: "cohort-1",
    area: "RRHH",
    score: 10,
    reason: "Excelente ruta, cambió completamente mi forma de trabajar. La IA ahora es parte de mi día a día.",
    improvement: "Más contenido sobre IA aplicada a gestión de talento y reclutamiento.",
    type: "promoter",
    submittedAt: "2025-01-11T11:00:00Z",
  },
  {
    id: "nps-6",
    userId: "u6",
    userName: "Pedro Ramírez",
    userEmail: "pedro@innovatesa.com",
    company: "Innovate SA",
    companyId: "org2",
    cohort: "Cohorte Enero 2025",
    cohortId: "cohort-1",
    area: "Ventas",
    score: 7,
    reason: "La ruta es bueno pero algunos videos son muy largos y pierdo concentración.",
    improvement: "Dividir los videos largos en segmentos más cortos de 10-15 minutos.",
    type: "passive",
    submittedAt: "2025-01-10T13:30:00Z",
  },
  {
    id: "nps-7",
    userId: "u7",
    userName: "Laura García",
    userEmail: "laura@digitallabs.com",
    company: "Digital Labs",
    companyId: "org3",
    cohort: "Cohorte Junio 2025",
    cohortId: "cohort-3",
    area: "Tecnología",
    score: 9,
    reason: "Muy actualizado con las últimas tendencias de IA. Las sesiones en vivo prácticas son excelentes.",
    improvement: "Incluir más proyectos colaborativos entre participantes.",
    type: "promoter",
    submittedAt: "2025-01-09T08:45:00Z",
  },
  {
    id: "nps-8",
    userId: "u8",
    userName: "Miguel Sánchez",
    userEmail: "miguel@techcorp.com",
    company: "Tech Corp",
    companyId: "org1",
    cohort: "Cohorte Marzo 2025",
    cohortId: "cohort-2",
    area: "Operaciones",
    score: 4,
    reason: "Esperaba más enfoque en automatización de procesos industriales.",
    improvement: "Agregar módulos específicos por industria (manufactura, logística, etc.).",
    type: "detractor",
    submittedAt: "2025-01-08T15:20:00Z",
  },
  {
    id: "nps-9",
    userId: "u9",
    userName: "Elena Vega",
    userEmail: "elena@innovatesa.com",
    company: "Innovate SA",
    companyId: "org2",
    cohort: "Cohorte Enero 2025",
    cohortId: "cohort-1",
    area: "Marketing",
    score: 10,
    reason: "La mejor inversión en formación que he hecho. El ROI es inmediato.",
    improvement: "Crear una comunidad más activa de alumni para networking.",
    type: "promoter",
    submittedAt: "2025-01-07T10:10:00Z",
  },
  {
    id: "nps-10",
    userId: "u10",
    userName: "Roberto Díaz",
    userEmail: "roberto@digitallabs.com",
    company: "Digital Labs",
    companyId: "org3",
    cohort: "Cohorte Junio 2025",
    cohortId: "cohort-3",
    area: "Finanzas",
    score: 8,
    reason: "Contenido de calidad, aunque me gustaría más profundidad en análisis de datos.",
    improvement: "Agregar módulos avanzados de Python y análisis predictivo.",
    type: "passive",
    submittedAt: "2025-01-06T14:00:00Z",
  },
];

// NPS trend data
const npsTrendData = [
  { month: "Oct 2024", nps: 58, responses: 45 },
  { month: "Nov 2024", nps: 62, responses: 52 },
  { month: "Dic 2024", nps: 65, responses: 48 },
  { month: "Ene 2025", nps: 72, responses: 68 },
];

// Platform colors - teal palette with red for negative
const CHART_COLORS = {
  promoter: "hsl(var(--primary))",      // Teal primary
  passive: "hsl(var(--secondary))",     // Purple/violet secondary
  detractor: "hsl(var(--destructive))", // Red for negative
};

// Score distribution
const getScoreDistribution = (responses: NPSResponse[]) => {
  const distribution = Array.from({ length: 10 }, (_, i) => ({
    score: i + 1,
    count: responses.filter(r => r.score === i + 1).length,
    fill: i + 1 >= 9 ? CHART_COLORS.promoter : i + 1 >= 7 ? CHART_COLORS.passive : CHART_COLORS.detractor,
  }));
  return distribution;
};

// Calculate NPS
const calculateNPS = (responses: NPSResponse[]) => {
  const promoters = responses.filter(r => r.score >= 9).length;
  const detractors = responses.filter(r => r.score <= 6).length;
  const total = responses.length;
  if (total === 0) return 0;
  return Math.round(((promoters - detractors) / total) * 100);
};

export default function NPSDashboardPage() {
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();
  const [selectedCohort, setSelectedCohort] = useCohortFilterStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "promoter" | "passive" | "detractor">("all");

  // Get available cohorts based on selected org
  const availableCohorts = cohortsByOrg[selectedOrg] || cohortsByOrg.all;

  // Filter responses
  const filteredResponses = mockNPSResponses.filter(response => {
    const matchesOrg = selectedOrg === "all" || response.companyId === selectedOrg;
    const matchesCohort = selectedCohort === "all" || response.cohortId === selectedCohort;
    const matchesSearch = searchTerm === "" || 
      response.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      response.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      response.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      response.improvement.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || response.type === typeFilter;
    return matchesOrg && matchesCohort && matchesSearch && matchesType;
  });

  // Calculate stats
  const npsScore = calculateNPS(filteredResponses);
  const promoterCount = filteredResponses.filter(r => r.type === "promoter").length;
  const passiveCount = filteredResponses.filter(r => r.type === "passive").length;
  const detractorCount = filteredResponses.filter(r => r.type === "detractor").length;
  const totalResponses = filteredResponses.length;

  const promoterPercent = totalResponses > 0 ? Math.round((promoterCount / totalResponses) * 100) : 0;
  const passivePercent = totalResponses > 0 ? Math.round((passiveCount / totalResponses) * 100) : 0;
  const detractorPercent = totalResponses > 0 ? Math.round((detractorCount / totalResponses) * 100) : 0;

  // Distribution for pie chart
  const pieData = [
    { name: "Promotores (9-10)", value: promoterCount, color: CHART_COLORS.promoter },
    { name: "Pasivos (7-8)", value: passiveCount, color: CHART_COLORS.passive },
    { name: "Detractores (1-6)", value: detractorCount, color: CHART_COLORS.detractor },
  ];

  const scoreDistribution = getScoreDistribution(filteredResponses);

  // Export CSV
  const handleExportCSV = () => {
    const headers = ["Usuario", "Email", "Empresa", "Cohorte", "Área", "Puntuación", "Tipo", "Razón", "Sugerencia de mejora", "Fecha"];
    const rows = filteredResponses.map(r => [
      r.userName,
      r.userEmail,
      r.company,
      r.cohort,
      r.area,
      r.score.toString(),
      r.type,
      `"${r.reason.replace(/"/g, '""')}"`,
      `"${r.improvement.replace(/"/g, '""')}"`,
      new Date(r.submittedAt).toLocaleDateString("es-ES"),
    ]);
    
    const csvContent = [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `nps_responses_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "promoter": return <ThumbsUp className="w-4 h-4 text-primary" />;
      case "passive": return <Meh className="w-4 h-4 text-secondary" />;
      case "detractor": return <ThumbsDown className="w-4 h-4 text-destructive" />;
      default: return null;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "promoter": return <Badge className="bg-primary/20 text-primary hover:bg-primary/30">Promotor</Badge>;
      case "passive": return <Badge className="bg-secondary/20 text-secondary hover:bg-secondary/30">Pasivo</Badge>;
      case "detractor": return <Badge className="bg-destructive/20 text-destructive hover:bg-destructive/30">Detractor</Badge>;
      default: return null;
    }
  };

  const getNPSColor = (score: number) => {
    if (score >= 50) return "text-primary";
    if (score >= 0) return "text-secondary";
    return "text-destructive";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            Dashboard NPS
          </h1>
          <p className="text-muted-foreground mt-1">
            Análisis de satisfacción de la ruta Líder IA
          </p>
        </div>
        <Button onClick={handleExportCSV} variant="outline" className="gap-2">
          <Download className="w-4 h-4" />
          Exportar CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="w-full md:w-48">
              <Select value={selectedOrg} onValueChange={(value) => {
                setSelectedOrg(value);
                setSelectedCohort("all");
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-48">
              <Select value={selectedCohort} onValueChange={setSelectedCohort}>
                <SelectTrigger>
                  <SelectValue placeholder="Cohorte" />
                </SelectTrigger>
                <SelectContent>
                  {availableCohorts.map(cohort => (
                    <SelectItem key={cohort.id} value={cohort.id}>{cohort.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-48">
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="promoter">Promotores</SelectItem>
                  <SelectItem value="passive">Pasivos</SelectItem>
                  <SelectItem value="detractor">Detractores</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, empresa o comentario..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">NPS Score</p>
                <p className={`text-4xl font-bold ${getNPSColor(npsScore)}`}>{npsScore}</p>
              </div>
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                <Target className="w-7 h-7 text-primary" />
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1 text-sm text-primary">
              <TrendingUp className="w-4 h-4" />
              <span>+7 vs mes anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Respuestas</p>
                <p className="text-4xl font-bold text-foreground">{totalResponses}</p>
              </div>
              <div className="w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center">
                <Users className="w-7 h-7 text-accent" />
              </div>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              91% tasa de respuesta
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Promotores</p>
                <p className="text-4xl font-bold text-primary">{promoterCount}</p>
              </div>
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                <ThumbsUp className="w-7 h-7 text-primary" />
              </div>
            </div>
            <p className="mt-2 text-sm text-primary">
              {promoterPercent}% del total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Detractores</p>
                <p className="text-4xl font-bold text-destructive">{detractorCount}</p>
              </div>
              <div className="w-14 h-14 rounded-full bg-destructive/20 flex items-center justify-center">
                <ThumbsDown className="w-7 h-7 text-destructive" />
              </div>
            </div>
            <p className="mt-2 text-sm text-destructive">
              {detractorPercent}% del total
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Distribución de Respuestas</CardTitle>
            <CardDescription>Promotores, Pasivos y Detractores</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Score Distribution Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Distribución por Puntuación</CardTitle>
            <CardDescription>¿Qué tan probable es que recomiendes la ruta?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scoreDistribution}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="score" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Respuestas" radius={[4, 4, 0, 0]}>
                    {scoreDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* NPS Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Tendencia del NPS</CardTitle>
          <CardDescription>Evolución del Net Promoter Score en los últimos meses</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={npsTrendData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="month" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="nps"
                  stroke="hsl(var(--primary))"
                  strokeWidth={3}
                  dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 6 }}
                  name="NPS"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Responses Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Respuestas Detalladas
          </CardTitle>
          <CardDescription>
            Las 3 preguntas de la encuesta NPS
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[180px]">Usuario</TableHead>
                  <TableHead className="w-[100px]">Empresa</TableHead>
                  <TableHead className="w-[80px] text-center">Puntuación</TableHead>
                  <TableHead className="w-[100px]">Tipo</TableHead>
                  <TableHead>Razón de la calificación</TableHead>
                  <TableHead>Sugerencias de mejora</TableHead>
                  <TableHead className="w-[100px]">Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResponses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                      No se encontraron respuestas con los filtros seleccionados
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredResponses.map((response) => (
                    <TableRow key={response.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{response.userName}</p>
                          <p className="text-xs text-muted-foreground">{response.area}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{response.company}</p>
                          <p className="text-xs text-muted-foreground">{response.cohort}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`text-xl font-bold ${
                          response.score >= 9 ? "text-primary" :
                          response.score >= 7 ? "text-secondary" : "text-destructive"
                        }`}>
                          {response.score}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTypeIcon(response.type)}
                          {getTypeBadge(response.type)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm line-clamp-2" title={response.reason}>
                          {response.reason}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <p className="text-sm line-clamp-2" title={response.improvement}>
                            {response.improvement}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(response.submittedAt).toLocaleDateString("es-ES")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
